import { useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useWorkflow } from '@/contexts/WorkflowContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Plus, GitBranch, MoreVertical, Pencil, Trash2, Play, Pause, ArrowLeft, Bot, ListChecks } from 'lucide-react'
import { PermissionGate } from '@/components/PermissionGate'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { WORKFLOW_STATUS_CONFIG } from '@/types/workflow'
import { PageTransition } from '@/components/PageTransition'

export function Workflows() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAgentMode = location.pathname.startsWith('/agents/workflows')
  const { workflows: allWorkflows, isLoading, createWorkflow, deleteWorkflow, activateWorkflow, pauseWorkflow } = useWorkflow()
  const [newWorkflowName, setNewWorkflowName] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  // Filter workflows by type based on route
  const workflows = useMemo(() =>
    allWorkflows.filter(w => {
      const wType = (w as unknown as { workflow_type?: string }).workflow_type || 'lead'
      return isAgentMode ? wType === 'agent' : wType === 'lead'
    }),
    [allWorkflows, isAgentMode]
  )

  const basePath = isAgentMode ? '/agents/workflows' : '/workflows'
  const pageTitle = isAgentMode ? 'Agent Workflows' : 'Lead Workflows'
  const pageDescription = isAgentMode
    ? 'Diseña workflows autónomos para tus agentes con decision paths y scheduling'
    : 'Build conditional LinkedIn automation flows'

  const handleCreate = async () => {
    if (!newWorkflowName.trim()) return
    setCreating(true)
    try {
      const workflow = await createWorkflow(newWorkflowName, isAgentMode ? 'agent' : 'lead')
      setIsCreateOpen(false)
      setNewWorkflowName('')
      if (workflow) {
        navigate(`${basePath}/${workflow.id}`)
      }
    } catch (error) {
      console.error('Failed to create workflow:', error)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar este workflow?')) {
      await deleteWorkflow(id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <PageTransition className={isAgentMode ? "min-h-screen bg-background" : ""}>
      {/* Agent mode: standalone header (no Chief Outreach sidebar) */}
      {isAgentMode && (
        <div className="border-b bg-background/95 backdrop-blur px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Agentes
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bot className="h-4 w-4" />
            <span>Agentes IA</span>
            <span>/</span>
            <span className="text-foreground font-medium">Workflows</span>
          </div>
        </div>
      )}
      <div className={isAgentMode ? "p-8" : "p-8"}>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-[28px] font-bold tracking-tight font-heading">{pageTitle}</h1>
          <p className="text-muted-foreground">{pageDescription}</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <PermissionGate permission="workflows_create">
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Workflow
              </Button>
            </DialogTrigger>
          </PermissionGate>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Nuevo Workflow</DialogTitle>
              <DialogDescription>
                Give your workflow a name to get started
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="workflow-name">Workflow Name</Label>
                <Input
                  id="workflow-name"
                  value={newWorkflowName}
                  onChange={(e) => setNewWorkflowName(e.target.value)}
                  placeholder={isAgentMode ? "e.g., Ventas Diarias Nando" : "e.g., Connection + Follow-up Flow"}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={creating || !newWorkflowName.trim()}>
                {creating ? 'Creando...' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {workflows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">Sin workflows aún</h3>
            <p className="mb-4 text-sm text-muted-foreground text-center max-w-md">
              {isAgentMode
                ? 'Crea tu primer workflow de agentes con skills, decision paths y scheduling automático'
                : 'Create your first workflow to build conditional LinkedIn automation flows with branching logic'
              }
            </p>
            <PermissionGate permission="workflows_create">
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workflow
              </Button>
            </PermissionGate>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {workflows.map((workflow) => {
            const nodeCount = workflow.graph_json?.nodes?.length || 0
            const statusConfig = WORKFLOW_STATUS_CONFIG[workflow.status]
            return (
              <Card
                key={workflow.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`${basePath}/${workflow.id}`)}
              >
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{workflow.name}</CardTitle>
                    <CardDescription>
                      {nodeCount} node{nodeCount !== 1 ? 's' : ''}
                      {workflow.description ? ` — ${workflow.description}` : ''}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          // CA-052: use basePath so agent-workflows open at
                          // /agents/workflows/:id (correct layout + back arrow),
                          // not the hardcoded lead-workflow path.
                          navigate(`${basePath}/${workflow.id}`)
                        }}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`${basePath}/${workflow.id}/runs`)
                        }}
                      >
                        <ListChecks className="mr-2 h-4 w-4" />
                        Runs
                      </DropdownMenuItem>
                      {workflow.status === 'draft' || workflow.status === 'paused' ? (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            activateWorkflow(workflow.id)
                          }}
                        >
                          <Play className="mr-2 h-4 w-4" />
                          Activate
                        </DropdownMenuItem>
                      ) : workflow.status === 'active' ? (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            pauseWorkflow(workflow.id)
                          }}
                        >
                          <Pause className="mr-2 h-4 w-4" />
                          Pause
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(workflow.id)
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Badge variant={workflow.status === 'active' ? 'default' : 'secondary'}>
                      {statusConfig.label}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(workflow.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
      </div>
    </PageTransition>
  )
}
